import React, {useCallback, useMemo} from 'react';

// Modules
import type {ContentType} from '@deezer/next/services/types/contentType';
import {
	COVER_DEFAULT_MD5,
	getClickURL,
	getCoverType,
	getImageMd5,
	getImageSrc,
	getSrcHeight,
	getSrcWidth,
} from './utils';
import type {BoxProps, ResponsiveValue} from '@tempo/core';
import {Box, Image} from '@tempo/core';
import {NavLink} from '@tempo/nextjs';
import {useRouter} from 'next/router';
import classnames from 'classnames';
import {useD4CToolsValue} from '@components/D4CTools/D4CToolsContext';
import {ConfigKeys} from '@components/D4CTools/types';

export type ThumbnailProps = {
	picture?: string;
	type?: ContentType;
	id?: string;
	rounded: boolean;
	height: ResponsiveValue<string | number>;
	width: ResponsiveValue<string | number>;
	coverLink: boolean;
	redirectUrl?: string;
	onClick?: () => void;
	isLoading?: boolean;
	isSelected?: boolean;
	isDisabled?: boolean;
	hasSharing?: boolean;
	src?: string;
} & Omit<BoxProps, 'rounded'>;

const HALO_STROKE_ROUNDED = 3;
const HALO_GAP_ROUNDED = 1;

const HALO_STROKE_SQUARE = 3;
const HALO_GAP_SQUARE = 1;

const Thumbnail = ({
	id,
	type,
	coverLink,
	redirectUrl,
	onClick,
	rounded,
	height,
	width,
	picture,
	isSelected,
	isLoading,
	isDisabled,
	hasSharing,
	src,
	...rest
}: ThumbnailProps): React.JSX.Element | null => {
	const disableThumbnails = useD4CToolsValue(ConfigKeys.DISABLE_THUMBNAILS);
	const router = useRouter();

	const thumbnailURL = useMemo(
		() => (type ? getClickURL(type, id) : undefined),
		[type, id],
	);

	const imgSrc = useMemo(() => {
		if (src) return src;

		const coverType = getCoverType(type);
		const imageMd5 = getImageMd5(
			disableThumbnails ? COVER_DEFAULT_MD5 : picture,
		);

		return getImageSrc(
			coverType,
			imageMd5,
			getSrcWidth(width),
			getSrcHeight(height),
		);
	}, [src, type, disableThumbnails, picture, width, height]);

	const onThumbnailClick = useCallback(() => {
		if (onClick) onClick();

		if (coverLink && type) {
			const url = redirectUrl || thumbnailURL;
			if (url) router.push(url);
		}
	}, [coverLink, onClick, redirectUrl, router, thumbnailURL, type]);

	const containerClasses = classnames({
		thumbnail: true,
		'thumbnail-rounded': rounded,
		'thumbnail-selected': isSelected || isLoading,
		'thumbnail-disabled': isDisabled,
		'thumbnail-clickable': coverLink || Boolean(onClick),
	});

	const figureClasses = classnames({
		picture: true,
		'picture-link': coverLink || Boolean(onClick),
	});

	const imgAttributes = useMemo(() => {
		return {
			className: 'picture-img',
			rounded: rounded ? '50%' : '4px',
			height,
			width,
		};
	}, [rounded, height, width]);

	const selectedTag = useCallback(() => {
		if (!isSelected && !isLoading) return null;

		return (
			<Box
				className={classnames('thumbnail-selected-indicator', {
					'thumbnail-selected-indicator--loading': isLoading,
				})}
			/>
		);
	}, [isLoading, isSelected]);

	const content = useMemo(() => {
		const imageComponent = (
			<Image
				{...imgAttributes}
				src={imgSrc}
				sx={
					hasSharing
						? {
								width: '100%',
								height: '100%',
								objectFit: 'cover',
							}
						: {}
				}
			/>
		);

		if (hasSharing) {
			const haloBorderRadius = rounded ? 'full' : '4px';

			const stroke = rounded ? HALO_STROKE_ROUNDED : HALO_STROKE_SQUARE;
			const gap = rounded ? HALO_GAP_ROUNDED : HALO_GAP_SQUARE;

			const borderInset = stroke;
			const imageInset = stroke + gap;

			return (
				<>
					{selectedTag()}
					<Box
						position="relative"
						display="inline-block"
						width={width}
						height={height}
					>
						<Box
							position="absolute"
							top="0"
							left="0"
							width="100%"
							height="100%"
							borderRadius={haloBorderRadius}
							overflow="hidden"
							sx={{
								'--halo-angle': '0deg',
								background: `conic-gradient(
								  from var(--halo-angle),
								  #A238FF 0%,
								  #A238FF 20%,
								  #AA3BE7 30%,
								  #B63ED0 40%,
								  #C941BA 50%,
								  #B63ED0 60%,
								  #AA3BE7 70%,
								  #A238FF 80%,
								  #A238FF 100%
								)`,
								animation: 'haloAngle 2s linear infinite',
								'@keyframes haloAngle': {
									'0%': {'--halo-angle': '0deg'},
									'100%': {'--halo-angle': '360deg'},
								},
								boxShadow: '0 0 8px rgba(162, 56, 255, 0.6)',
								willChange: 'background',
							}}
						/>

						<Box
							position="absolute"
							top={`${borderInset}px`}
							left={`${borderInset}px`}
							right={`${borderInset}px`}
							bottom={`${borderInset}px`}
							borderRadius={haloBorderRadius}
							bg="#FFFFFF"
						/>

						<Box
							position="absolute"
							top={`${imageInset}px`}
							left={`${imageInset}px`}
							right={`${imageInset}px`}
							bottom={`${imageInset}px`}
							borderRadius={haloBorderRadius}
							overflow="hidden"
						>
							{imageComponent}
						</Box>
					</Box>
				</>
			);
		}

		return (
			<>
				{selectedTag()}
				<Box className={figureClasses}>{imageComponent}</Box>
			</>
		);
	}, [
		selectedTag,
		figureClasses,
		imgAttributes,
		imgSrc,
		hasSharing,
		width,
		height,
		rounded,
	]);

	if (!onClick && coverLink && (redirectUrl || type)) {
		const url = redirectUrl || thumbnailURL;
		if (url) {
			return (
				<NavLink href={url}>
					<Box as={'figure'} className={containerClasses} {...rest}>
						{content}
					</Box>
				</NavLink>
			);
		}
	}

	return (
		<Box
			as="figure"
			className={containerClasses}
			onClick={onThumbnailClick}
			{...rest}
		>
			{content}
		</Box>
	);
};

Thumbnail.defaultProps = {
	rounded: false,
	height: 250,
	width: 250,
	coverLink: true,
};

export default Thumbnail;
